/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react"
import type { RefObject } from "react"

import { type AnimationScope, useAnimate, useReducedMotion } from "framer-motion"
import includes from "lodash-es/includes"
import { useIntersection } from "react-use"

import { EAppAnimationName, type IAppState, TAppDispatcher, useAppDispatcher, useAppSelector } from "@/3514/store/app"

type _TParams = {
    animationName: EAppAnimationName
    isDisabled?: boolean
}
type TAnimatedSectionState = {
    containerRef: RefObject<HTMLDivElement>
    shouldAnimate: boolean
    hasPlayed: boolean
    isMotionReduced: boolean
    animateContainer: Function
}

function useAnimatedSection(params: _TParams): TAnimatedSectionState {
    const [containerRef, animateContainer]: [AnimationScope<HTMLDivElement>, Function] = useAnimate()

    const isMotionReduced: boolean = useReducedMotion()

    const entry: IntersectionObserverEntry = useIntersection(containerRef, {
        root: null,
        rootMargin: "10px 0px 0px 0px",
        threshold: 0.1
    })

    const appDispatcher: TAppDispatcher = useAppDispatcher()

    const { animations }: IAppState = useAppSelector()

    const isVisible: boolean = !!entry?.isIntersecting

    const hasPlayed: boolean = includes<EAppAnimationName>(animations, params.animationName)

    const shouldAnimate: boolean = isVisible && !hasPlayed && !isMotionReduced

    useEffect((): void => {
        if (params.isDisabled) return

        if (isVisible && !hasPlayed) appDispatcher.completeAnimation(params.animationName)
    }, [isVisible, hasPlayed, params.animationName, params.isDisabled])

    return {
        containerRef,
        shouldAnimate,
        hasPlayed,
        isMotionReduced,
        animateContainer
    }
}

export { useAnimatedSection }
export type { TAnimatedSectionState }
